import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { CloudRain, Sun, CloudSnow, Cloud, Lightning, Wind } from "@phosphor-icons/react";

/**
 * WeatherWidget — live weather card powered by the existing
 * `GET /api/weather/forecast` endpoint (Open-Meteo, tenant default location).
 *
 * Cached 10 minutes in localStorage to avoid hammering the API when the
 * dashboard re-renders. Shows current-day conditions prominently + a
 * 6-day mini forecast strip below.
 *
 * Props:
 *  - variant: "card" (default, full widget) | "strip" (compact horizontal)
 *  - tone: "gradient" (default) | "light"
 *  - days: how many days to pull (default 7)
 *  - testid: data-testid prefix (default "weather-widget")
 */

const CACHE_KEY = "fr_weather_forecast_cache";
const CACHE_TTL_MS = 10 * 60 * 1000;

function iconFor(code, size = 20) {
  // Open-Meteo WMO code → phosphor icon
  if (code == null) return <Cloud size={size} weight="fill"/>;
  if (code === 0) return <Sun size={size} weight="fill"/>;
  if (code >= 1 && code <= 3) return <Cloud size={size} weight="fill"/>;
  if (code >= 45 && code <= 48) return <Cloud size={size} weight="fill"/>;
  if (code >= 51 && code <= 67) return <CloudRain size={size} weight="fill"/>;
  if (code >= 71 && code <= 77) return <CloudSnow size={size} weight="fill"/>;
  if (code >= 80 && code <= 82) return <CloudRain size={size} weight="fill"/>;
  if (code >= 85 && code <= 86) return <CloudSnow size={size} weight="fill"/>;
  if (code >= 95) return <Lightning size={size} weight="fill"/>;
  return <Cloud size={size} weight="fill"/>;
}

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, data } = JSON.parse(raw);
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return data;
  } catch { return null; }
}
function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data })); } catch {}
}

function dayLabel(iso) {
  try {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short" });
  } catch { return iso; }
}

export default function WeatherWidget({ variant = "card", tone = "gradient", days = 7, testid = "weather-widget" }) {
  const [forecast, setForecast] = useState(() => readCache());
  const [loading, setLoading] = useState(!forecast);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/weather/forecast?days=${days}`);
      setForecast(r.data);
      writeCache(r.data);
      setError(false);
    } catch {
      setError(true);
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => {
    // Refresh in the background even if we have cached data, but don't block first paint.
    load();
  }, [load]);

  if (loading && !forecast) {
    return (
      <div data-testid={`${testid}-loading`} className="rounded-2xl bg-slate-100 h-32 animate-pulse"/>
    );
  }
  if (error && !forecast) {
    return (
      <div data-testid={`${testid}-error`}
        className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-[11px] font-black uppercase tracking-widest text-slate-400">
        Weather unavailable
      </div>
    );
  }

  const today = forecast?.days?.[0];
  const rest = (forecast?.days || []).slice(1, days);
  const loc = forecast?.location || {};

  // Compact strip mode: single row, wraps in constrained containers
  if (variant === "strip") {
    return (
      <div data-testid={testid}
        className="rounded-xl bg-white border border-slate-200 shadow-sm px-3 py-2 flex items-center gap-3 overflow-x-auto">
        {(forecast?.days || []).slice(0, days).map((d, i) => (
          <div key={d.date} className="flex items-center gap-1.5 shrink-0" data-testid={`${testid}-day-${i}`}>
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 w-8">{i === 0 ? "Today" : dayLabel(d.date)}</span>
            <span className={d.is_wet ? "text-cyan-600" : "text-amber-500"}>{iconFor(d.code, 16)}</span>
            <span className="text-xs font-black text-slate-900 tabular-nums">{d.hi}°</span>
            <span className="text-[10px] text-slate-400 tabular-nums">/{d.lo}°</span>
            {d.precip_prob >= 30 && (
              <span className="text-[9px] font-black text-cyan-700 tabular-nums">{d.precip_prob}%</span>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Card mode — hero + forecast strip
  const isGradient = tone === "gradient";
  const bg = isGradient
    ? "bg-gradient-to-br from-sky-500 via-cyan-500 to-emerald-500 text-white"
    : "bg-white border border-slate-200 shadow-sm text-slate-900";

  return (
    <div data-testid={testid}
      className={`rounded-2xl overflow-hidden ${bg}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className={`text-[10px] font-black uppercase tracking-widest ${isGradient ? "text-white/80" : "text-slate-500"}`}>
              Field weather · {loc.label || loc.city || "Local"}
            </div>
            <div className="mt-1 flex items-end gap-3">
              <div className={`text-5xl font-black tabular-nums ${isGradient ? "text-white" : "text-slate-900"}`}
                data-testid={`${testid}-current-temp`}>
                {today?.hi ?? "—"}°
              </div>
              <div className="pb-1.5">
                <div className={`text-sm font-black ${isGradient ? "text-white" : "text-slate-800"}`}>{today?.label || "—"}</div>
                <div className={`text-[11px] ${isGradient ? "text-white/85" : "text-slate-500"}`}>
                  Low {today?.lo ?? "—"}°
                  {today?.precip_prob != null && today.precip_prob > 0 && <> · {today.precip_prob}% precip</>}
                  {today?.wind_max != null && today.wind_max > 0 && <> · {today.wind_max} mph wind</>}
                </div>
              </div>
            </div>
          </div>
          <div className={`h-14 w-14 rounded-2xl flex items-center justify-center ${isGradient ? "bg-white/20 backdrop-blur text-white" : "bg-slate-100 text-slate-700"}`}>
            {iconFor(today?.code, 30)}
          </div>
        </div>

        {today?.is_wet && (
          <div className={`mt-3 text-[10px] font-black uppercase tracking-widest ${isGradient ? "text-white bg-white/20" : "text-cyan-700 bg-cyan-50 border border-cyan-100"} rounded-full px-2.5 py-1 inline-flex items-center gap-1.5`}
            data-testid={`${testid}-wet-flag`}>
            <CloudRain size={12} weight="fill"/> Wet day — plan indoor WOs
          </div>
        )}
        {!today?.is_wet && today?.wind_max >= 20 && (
          <div className={`mt-3 text-[10px] font-black uppercase tracking-widest ${isGradient ? "text-white bg-white/20" : "text-amber-700 bg-amber-50 border border-amber-100"} rounded-full px-2.5 py-1 inline-flex items-center gap-1.5`}
            data-testid={`${testid}-wind-flag`}>
            <Wind size={12} weight="fill"/> Windy — skip high-lift roof work
          </div>
        )}
      </div>

      {rest.length > 0 && (
        <div className={`px-2 py-2 grid grid-cols-6 gap-1 ${isGradient ? "bg-black/10" : "bg-slate-50 border-t border-slate-200"}`}>
          {rest.map((d, i) => (
            <div key={d.date} className={`text-center py-1.5 rounded-lg ${isGradient ? "" : ""}`}
              data-testid={`${testid}-day-${i + 1}`}>
              <div className={`text-[9px] font-black uppercase tracking-widest ${isGradient ? "text-white/70" : "text-slate-500"}`}>
                {dayLabel(d.date)}
              </div>
              <div className={`my-1 flex justify-center ${isGradient ? (d.is_wet ? "text-white" : "text-white/90") : (d.is_wet ? "text-cyan-600" : "text-amber-500")}`}>
                {iconFor(d.code, 16)}
              </div>
              <div className={`text-[11px] font-black tabular-nums ${isGradient ? "text-white" : "text-slate-800"}`}>
                {d.hi}°
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
